import { Container, Sprite } from 'pixi.js';
import * as M from '../core/mapfx';
import { rand } from '../core/pixelfx';

/**
 * 끝없는 맵 — 사천 땅의 야생.
 *
 * 세계를 CHUNK 크기 칸으로 나누고, 화면에 걸친 칸에만 장식을 놓는다. 벗어난 칸은 걷는다.
 * 칸마다 무엇을 어디에 놓을지는 **칸 좌표만으로** 정한다 — 같은 자리는 언제 와도 같은 모습이고,
 * 게임 난수(rng)를 안 건드리므로 같은 시드가 같은 판이 되는 것도 그대로다.
 *
 * 장식은 전부 판정이 없고(지나갈 수 있다) 적·탄·보석보다 아래 층에 그린다 — 나무가 적을 가리면 안 된다.
 */

const CHUNK = 512;
/** 나무는 밑동에서 위로 240px 넘게 솟으므로 화면 밖 칸도 조금 더 깔아 둔다 */
const MARGIN = 300;

/**
 * 크게 그리는 장식. 그림은 작게 찍고 정수 배로 키운다(최근접이라 도트가 고르게 굵어진다).
 * 1배일 때 나무가 캐릭터(약 70px)의 1.5배뿐이라 나무가 아니라 화분처럼 보였다 — 2배면 3~3.5배.
 */
const SCALE: Partial<Record<Kind, number>> = { pine: 2, cypress: 2, camphor: 2, bamboo: 2 };

/** 정수 좌표 → 0~1. 좌표마다 늘 같은 값 */
function hash2(x: number, y: number, s: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(s | 0, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** 칸 좌표 위의 부드러운 노이즈 — 대숲·숲·돌밭이 여러 칸에 걸쳐 뭉치게 */
function smooth(x: number, y: number, s: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hash2(x0, y0, s);
  const b = hash2(x0 + 1, y0, s);
  const c = hash2(x0, y0 + 1, s);
  const d = hash2(x0 + 1, y0 + 1, s);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}

type Kind =
  | 'bamboo' | 'pine' | 'cypress' | 'camphor' | 'bush' | 'rockS' | 'rockL' | 'grass' | 'pebbles'
  | 'soil' | 'moss' | 'puddle' | 'leaves' | 'mist';

/** 어느 층에 그리나: 바닥에 눕는 것 / 서 있는 것(밑동 y 순서) / 안개 */
const FLAT = new Set<Kind>(['soil', 'moss', 'puddle', 'leaves', 'pebbles']);

interface Placed {
  kind: Kind;
  v: number;
  x: number;
  y: number;
  flip: boolean;
  alpha: number;
}

export class WorldMap {
  /** 흙·이끼 자국, 웅덩이, 낙엽, 잔돌 */
  readonly flatLayer = new Container();
  /** 풀·바위·덤불·나무·대숲 — 밑동이 아래인 것이 앞에 온다 */
  readonly standLayer = new Container();
  readonly mistLayer = new Container();

  private readonly art: Record<Kind, M.Prop[]>;
  private readonly chunks = new Map<string, Sprite[]>();
  private readonly free: Sprite[] = [];
  private lastRange = '';

  constructor() {
    this.standLayer.sortableChildren = true;
    const n = (k: number): number[] => Array.from({ length: k }, (_, i) => i);
    this.art = {
      bamboo: n(3).map(M.bamboo),
      pine: n(2).map(M.pine),
      cypress: n(2).map(M.cypress),
      camphor: n(2).map(M.camphor),
      bush: n(3).map(M.bush),
      rockS: n(3).map((s) => M.rock(s, 6 + s * 2)),
      rockL: n(2).map((s) => M.rock(s + 7, 15 + s * 5)),
      grass: n(6).map(M.grass),
      pebbles: n(3).map(M.pebbles),
      soil: n(3).map(M.soilPatch),
      moss: n(3).map(M.mossPatch),
      puddle: n(2).map(M.puddle),
      leaves: n(3).map(M.leaves),
      mist: n(2).map(M.mist),
    };
  }

  /** 카메라 중심(cx, cy)과 화면 크기에 맞춰 칸을 깔고 걷는다. 매 프레임 불러도 칸이 바뀔 때만 일한다. */
  update(cx: number, cy: number, viewW: number, viewH: number): void {
    const x0 = Math.floor((cx - viewW / 2 - MARGIN) / CHUNK);
    const x1 = Math.floor((cx + viewW / 2 + MARGIN) / CHUNK);
    const y0 = Math.floor((cy - viewH / 2 - MARGIN) / CHUNK);
    const y1 = Math.floor((cy + viewH / 2 + MARGIN) / CHUNK);
    const range = `${x0},${y0},${x1},${y1}`;
    if (range === this.lastRange) return;
    this.lastRange = range;

    for (const [key, sprites] of this.chunks) {
      const [ix, iy] = key.split(',').map(Number);
      if (ix >= x0 && ix <= x1 && iy >= y0 && iy <= y1) continue;
      for (const sp of sprites) {
        sp.parent?.removeChild(sp);
        this.free.push(sp);
      }
      this.chunks.delete(key);
    }
    for (let iy = y0; iy <= y1; iy++) {
      for (let ix = x0; ix <= x1; ix++) {
        const key = `${ix},${iy}`;
        if (!this.chunks.has(key)) this.chunks.set(key, this.build(ix, iy));
      }
    }
  }

  private build(ix: number, iy: number): Sprite[] {
    const out: Sprite[] = [];
    for (const p of populate(ix, iy)) {
      const art = this.art[p.kind][p.v % this.art[p.kind].length];
      const sp = this.free.pop() ?? new Sprite();
      sp.texture = art.texture;
      sp.anchor.set(art.ax, art.ay);
      sp.position.set(Math.round(p.x), Math.round(p.y));
      const k = SCALE[p.kind] ?? 1;
      sp.scale.set(p.flip ? -k : k, k);
      sp.alpha = p.alpha;
      sp.zIndex = Math.round(p.y);
      (p.kind === 'mist' ? this.mistLayer : FLAT.has(p.kind) ? this.flatLayer : this.standLayer).addChild(sp);
      out.push(sp);
    }
    return out;
  }
}

/** 칸 하나에 놓을 것들. 칸 좌표만으로 정해진다. */
function populate(ix: number, iy: number): Placed[] {
  const r = rand(Math.floor(hash2(ix, iy, 77) * 4294967295));
  const forest = smooth(ix * 0.35, iy * 0.35, 1); // 나무가 많은 곳
  const grove = smooth(ix * 0.3 + 50, iy * 0.3 - 20, 2); // 대숲
  const rocky = smooth(ix * 0.4 - 9, iy * 0.4 + 31, 3); // 돌밭
  const ox = ix * CHUNK;
  const oy = iy * CHUNK;
  const out: Placed[] = [];
  const tall: Array<[number, number]> = [];

  const put = (kind: Kind, count: number, opts: { spacing?: number; alpha?: [number, number] } = {}): void => {
    for (let i = 0; i < count; i++) {
      let x = 0;
      let y = 0;
      let ok = false;
      for (let tries = 0; tries < 8 && !ok; tries++) {
        x = ox + r() * CHUNK;
        y = oy + r() * CHUNK;
        ok = true;
        if (opts.spacing) {
          // 시작 자리는 트여 있게, 큰 것끼리는 너무 붙지 않게
          if (Math.hypot(x, y) < 260) ok = false;
          for (const [tx, ty] of tall) if (Math.hypot(x - tx, (y - ty) * 1.4) < opts.spacing) ok = false;
        }
      }
      if (!ok) continue;
      if (opts.spacing) tall.push([x, y]);
      const a = opts.alpha ? opts.alpha[0] + r() * (opts.alpha[1] - opts.alpha[0]) : 1;
      out.push({ kind, v: Math.floor(r() * 6), x, y, flip: r() < 0.5, alpha: a });
    }
  };
  const n = (base: number, extra: number): number => base + Math.floor(r() * (extra + 1));

  // 바닥에 눕는 것
  put('soil', (r() < 0.5 ? 1 : 0) + (rocky > 0.6 ? 1 : 0));
  put('moss', n(1, forest > 0.5 ? 1 : 0));
  put('leaves', forest > 0.45 ? n(1, 2) : r() < 0.35 ? 1 : 0);
  put('puddle', r() < 0.16 ? 1 : 0);
  put('pebbles', n(2, 2));

  // 서 있는 것 (작은 것)
  put('grass', n(8, 7) + (forest < 0.4 ? 5 : 0));
  put('rockS', n(1, 1) + (rocky > 0.6 ? 2 : 0));
  put('rockL', rocky > 0.55 ? n(1, 1) : r() < 0.2 ? 1 : 0, { spacing: 60 });
  put('bush', n(0, forest < 0.5 ? 2 : 1), { spacing: 40 });

  // 큰 것 — 나무와 대숲
  const trees = forest > 0.58 ? n(2, 2) : forest > 0.42 ? n(0, 1) : r() < 0.3 ? 1 : 0;
  for (let i = 0; i < trees; i++) {
    const k = r();
    put(k < 0.4 ? 'pine' : k < 0.7 ? 'cypress' : 'camphor', 1, { spacing: 160 });
  }
  put('bamboo', grove > 0.6 ? n(2, 3) : grove > 0.5 ? 1 : 0, { spacing: 95 });

  // 옅은 안개 (드물게)
  put('mist', r() < 0.1 ? 1 : 0, { alpha: [0.08, 0.13] });
  return out;
}
